/**
 * MapModeSelector - Collapsible map mode selection panel
 *
 * Allows switching between GeoTIFF-only and Satellite+GeoTIFF modes.
 */

import React from 'react';
import { Map as MapIcon, ChevronUp, ChevronDown } from 'lucide-react';

type MapMode = 'geotiff-only' | 'google-maps' | 'leaflet-satellite' | 'street-map';

interface MapModeSelectorProps {
  mapMode: MapMode;
  isOpen: boolean;
  onToggleOpen: () => void;
  onSelectMode: (mode: MapMode) => void;
}

export function MapModeSelector({
  mapMode,
  isOpen,
  onToggleOpen,
  onSelectMode,
}: MapModeSelectorProps) {
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={onToggleOpen}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <MapIcon className="h-5 w-5 text-gray-700" />
          <span className="font-semibold text-gray-900">Map Mode</span>
        </div>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className="p-6 pt-0 space-y-2">
          <button
            onClick={() => onSelectMode('geotiff-only')}
            className={`w-full py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
              mapMode === 'geotiff-only'
                ? 'bg-[#EBFD03] text-black border-2 border-black'
                : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
            }`}
          >
            GeoTIFF Only
          </button>
          <button
            onClick={() => onSelectMode('leaflet-satellite')}
            className={`w-full py-3 px-4 rounded-lg text-sm font-medium transition-colors ${
              mapMode === 'leaflet-satellite'
                ? 'bg-[#EBFD03] text-black border-2 border-black'
                : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
            }`}
          >
            Satellite + GeoTIFF
          </button>
        </div>
      )}
    </div>
  );
}

export default MapModeSelector;
